import { useStores } from 'utility/hooks/useStores';
import { toJS } from 'mobx';

import {
  FlightsJourneyCriteriaInput,
  FlightsSearchPreferencesInput,
  FlightsSortOrder,
  FlightsSortType,
  FlightsTravelerDetailsInput
} from '__generated__/api';
import { FlightsListings, FlightsListingsInputs } from '@shared-ui/retail-flights-smart-shopping-listings';
type SmartShoppingListingsInputsType = React.ComponentProps<typeof FlightsListings>['inputs'];
type TravelerDetailsInputType = SmartShoppingListingsInputsType['travelerDetails'];
type JourneyCriteriasInputType = SmartShoppingListingsInputsType['journeyCriteria'];
type FlightsSortOptionTypeInput = SmartShoppingListingsInputsType['sortOption'];
type FlightSearchContextInputsType = SmartShoppingListingsInputsType['flightsSearchContext'];
type SearchPreferencesInputsType = SmartShoppingListingsInputsType['searchPreferences'];
type TravelerDetailInputType = TravelerDetailsInputType[number];
type JourneyCriteriaInputType = JourneyCriteriasInputType[number];
type FlightSearchFilterValuesInput = SmartShoppingListingsInputsType['flightSearchFilterValuesList'];

export const useSmartShoppingListingsInput = (): FlightsListingsInputs => {
  const { searchCriteriaStore } = useStores();

  const {
    flightsSearchContext,
    travelerDetails,
    journeyCriteria,
    searchPreferences,
    searchFilterValuesList,
    sortOption
  } = searchCriteriaStore.searchCriteria;

  const { cabinClass, advancedFilters } = searchPreferences;

  const { journeysContinuationId, originalBookingId, tripType } = flightsSearchContext;

  const travelerDetailsMapped: TravelerDetailsInputType = (travelerDetails as FlightsTravelerDetailsInput[]).map(
    (travelerDetail): TravelerDetailInputType => ({
      count: travelerDetail.count,
      travelerType: (travelerDetail.travelerType as unknown) as TravelerDetailInputType['travelerType'],
      ages: travelerDetail.ages ?? null
    })
  );

  const journeyCriteriaMapped: JourneyCriteriasInputType = (journeyCriteria as FlightsJourneyCriteriaInput[]).map(
    (journeyCriteria): JourneyCriteriaInputType => ({
      departureDate: {
        day: journeyCriteria.departureDate.day,
        month: journeyCriteria.departureDate.month,
        year: journeyCriteria.departureDate.year
      },
      destination: journeyCriteria.destination,
      origin: journeyCriteria.origin
    })
  );

  const airline = (searchPreferences: FlightsSearchPreferencesInput): string => {
    if (searchPreferences.airline === '*') {
      return '';
    }

    return searchPreferences.airline;
  };

  const defaultSortOption = ({
    sortOrder: FlightsSortOrder.INCREASING,
    sortType: FlightsSortType.BEST_BETS
  } as unknown) as FlightsSortOptionTypeInput;

  return {
    journeyCriteria: journeyCriteriaMapped,
    travelerDetails: travelerDetailsMapped,
    searchPreferences: {
      advancedFilters: ((toJS(advancedFilters) as unknown) as SearchPreferencesInputsType['advancedFilters']) ?? [],
      airline: airline(searchPreferences),
      cabinClass: (cabinClass as unknown) as SearchPreferencesInputsType['cabinClass']
    },
    shoppingContext: null,
    sortOption: ((toJS(sortOption) as unknown) as FlightsSortOptionTypeInput) ?? defaultSortOption,
    flightSearchFilterValuesList: toJS(searchFilterValuesList) as FlightSearchFilterValuesInput,
    flightsSearchContext: {
      tripType: (tripType as unknown) as FlightSearchContextInputsType['tripType'],
      journeysContinuationId: journeysContinuationId,
      originalBookingId: originalBookingId
    }
  };
};
